import { exposeWhenVisible, isForced, setTreatmentShare, track, variantOf } from '@theme/hds-experiments';

/**
 * Vergelijker.
 *
 * Twee kanten:
 * 1. Collectiepagina — het vinkje uit snippets/vergelijk-vinkje.liquid. De specificaties
 *    halen we per pagina op via <collectie-URL>?view=vergelijkdata, buiten het raster om
 *    (SHO-11729); die data zegt ook welke kaarten een vinkje krijgen. Elders staan ze nog als
 *    JSON in de kaart. Die hele set onthouden we in localStorage.
 * 2. /pages/vergelijken — sections/vergelijk-tabel.liquid levert een lege romp plus de
 *    rijconfiguratie; hier bouwen we de tabel uit wat er in localStorage staat.
 *
 * Gevolg van deze aanpak: een gedeelde link werkt alleen bij iemand die de producten
 * zelf heeft aangevinkt. Wie een vreemde link opent, krijgt daarover een melding.
 * Wil je koude links laten werken, dan is een publieke Storefront API-token nodig.
 *
 * A/B-test (SHO-6410). De thema-instelling «Vergelijker → Tonen» zet het vinkje uit, altijd
 * aan, of in een test. In de test rendert het vinkje in BEIDE armen verborgen en beslist dit
 * script wie het te zien krijgt. Dat moet hier en niet in Liquid: collectie-HTML wordt aan de
 * rand gecachet, dus een Liquid-tak per bezoeker geeft vervuilde cache en flikkerende kaarten,
 * en de control-arm zou zonder markering in de HTML geen noemer hebben.
 */

const SLEUTEL = 'hds-vergelijk-v1';
/* Geen bovengrens op de selectie, net als bij Coolblue: je voegt zoveel producten toe
   als je wilt. Hoeveel kolommen er tegelijk in beeld staan bepaalt pasKolommenAan()
   verderop; de rest bereik je door horizontaal te vegen. */
const MIN = 2;

/* Het checkmark-pad komt uit assets/icon-checkmark.svg, zodat een in JS gebouwd
   vinkje er hetzelfde uitziet als een vinkje uit snippets/checkbox.liquid. */
const VINK_SVG =
  '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" class="icon-checkmark" aria-hidden="true">' +
  '<path d="M4.75439 10.7485L7.68601 14.5888C7.79288 14.7288 7.84632 14.7988 7.91174 14.8242C7.96907 14.8466 8.03262 14.8469 8.09022 14.8253C8.15596 14.8007 8.21026 14.7314 8.31886 14.5927L15.2475 5.74658" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"/></svg>';

const KRUIS_SVG =
  '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">' +
  '<path d="M3 3l8 8M11 3l-8 8" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>';

/* Prijsregel in de volgorde van de productpagina: eerst de prijs die je nu betaalt,
   daarna de oude prijs doorgestreept met het kortingspercentage ernaast. */
const prijsHtml = (item) => {
  let uit = esc(item.prijs);
  if (item.prijs_voor) {
    uit += ' <s class="hds-vgl-voor">' + esc(item.prijs_voor) + '</s>';
  }
  if (item.korting) {
    uit += ' <span class="hds-vgl-korting">-' + esc(item.korting) + '%</span>';
  }
  return uit;
};

const esc = (waarde) =>
  String(waarde ?? '').replace(/[&<>"]/g, (teken) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[teken]);

function lees() {
  try {
    const ruw = JSON.parse(localStorage.getItem(SLEUTEL));
    return Array.isArray(ruw) ? ruw : [];
  } catch {
    return [];
  }
}

function schrijf(lijst) {
  try {
    localStorage.setItem(SLEUTEL, JSON.stringify(lijst));
  } catch {
    /* privémodus of volle opslag: dan werkt de selectie alleen op deze pagina */
  }
  document.dispatchEvent(new CustomEvent('hds:vergelijk-gewijzigd'));
}

const paginaUrl = (lijst) => {
  const basis = document.documentElement.dataset.vglPagina || '/pages/vergelijken';
  return lijst.length ? `${basis}?producten=${lijst.map((i) => i.handle).join(',')}` : basis;
};

/* -------------------------------------------------------------------- A/B-test */

/** @type {{ id: string, arm: string, toon: boolean }} */
const experiment = { id: '', arm: '', toon: true };

/**
 * Het element waar de blootstelling aan hangt. Nadrukkelijk NIET de vinkje-wrapper zelf:
 * die staat in de control-arm op `hidden`, en een element met display:none komt bij een
 * IntersectionObserver nooit in beeld — dan zou precies de helft van de noemer ontbreken.
 * De productkaart eromheen staat in beide armen op dezelfde plek met dezelfde afmeting.
 *
 * @param {Element} kaart
 * @returns {Element}
 */
function anker(kaart) {
  return kaart.closest('product-card, .product-card, .product-grid__item') ?? kaart.parentElement ?? kaart;
}

/**
 * Deelt de bezoeker in zodra de eerste kaart er echt staat, en zegt of het vinkje getoond
 * mag worden. Pas op dat moment, want bij een morph komen de kaarten later en hoort de
 * blootstelling bij het zien van een kaart — niet bij het laden van dit script.
 *
 * Idempotent: een tweede aanroep geeft dezelfde uitkomst zonder opnieuw te meten.
 *
 * @param {Element} kaart
 * @returns {boolean} mag het vinkje zichtbaar zijn
 */
function bepaalArm(kaart) {
  if (experiment.arm) return experiment.toon;

  experiment.id = kaart.getAttribute('data-vergelijk-experiment') || '';
  if (!experiment.id) {
    // Geen test: het vinkje staat er voor iedereen.
    experiment.arm = 'treatment';
    return true;
  }

  // Het aandeel met vinkje uit «Vergelijker → Aandeel met vinkje»; vóór de indeling zetten.
  setTreatmentShare(experiment.id, kaart.getAttribute('data-vergelijk-aandeel') ?? '');
  experiment.arm = exposeWhenVisible(experiment.id, anker(kaart), { placement: 'collectiekaart' });
  experiment.toon = experiment.arm === 'treatment';
  return experiment.toon;
}

/**
 * Eén event naar de pixel, altijd met de arm erbij. Zonder lopende test blijven
 * `experiment_id` en `variant` leeg, zodat het gebruik ook ná de test meetbaar blijft —
 * dezelfde afspraak als bij de advies-drawer (SHO-10420).
 *
 * @param {string} naam
 * @param {Record<string, unknown>} velden
 */
function meld(naam, velden) {
  track(naam, {
    experiment_id: experiment.id,
    variant: experiment.id ? variantOf(experiment.id) : '',
    ...velden,
  });
}

/**
 * Zet één keer per sessie een cart-attribuut zodra iemand de vergelijker echt gebruikt
 * (doorklik vanuit de tabel). Daarmee is achteraf op orderregels te zien wat een bestelling
 * van een vergelijker-gebruiker opleverde — buiten GA4 om, dus ook bij geweigerde cookies.
 * Meten mag nooit een klik breken: alles faalt stil.
 *
 * `keepalive`, want dit vertrekt op de klik naar de productpagina: zonder breekt de browser
 * het verzoek af zodra de navigatie begint en komt het attribuut nooit aan. De sessievlag
 * pas na een geslaagd antwoord, anders is één afgebroken poging de enige. Een afgedwongen
 * arm (`?hds_exp=`) telt nergens mee, dus ook hier niet.
 */
function onthoudGebruik() {
  if (experiment.id && isForced(experiment.id)) return;

  const sleutel = 'hds_vgl_gebruikt';
  try {
    if (sessionStorage.getItem(sleutel) === '1') return;
  } catch {
    // Geen opslag: dan hooguit een extra schrijfactie, geen reden om over te slaan.
  }

  fetch('/cart/update.js', {
    method: 'POST',
    keepalive: true,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ attributes: { _vergelijker: 'gebruikt' } }),
  })
    .then((antwoord) => {
      if (!antwoord.ok) return;
      try {
        sessionStorage.setItem(sleutel, '1');
      } catch {
        // niet erg: hooguit een extra schrijfactie
      }
    })
    .catch(() => {
      /* winkelen mag hier nooit op stuklopen */
    });
}

/* ------------------------------------------------------------------ collectie */

/** @type {Map<string, Promise<Record<string, any>>>} */
const perPagina = new Map();

/** Vergelijkdata van één collectiepagina, één verzoek per pagina (zelfde patroon als staaldata). */
function haalPagina(pagina) {
  const url = new URL(location.href);
  url.hash = '';
  url.searchParams.delete('section_id');
  url.searchParams.set('view', 'vergelijkdata');
  if (pagina) url.searchParams.set('page', pagina);
  const sleutel = url.toString();
  let verzoek = perPagina.get(sleutel);
  if (!verzoek) {
    verzoek = fetch(url, { credentials: 'same-origin' })
      .then((res) => (res.ok ? res.json() : {}))
      .catch(() => ({}));
    perPagina.set(sleutel, verzoek);
  }
  return verzoek;
}

/** Het handle van de kaart, zonder de data te laden (de waarde van het vinkje). */
function kaartHandle(kaart) {
  return kaart.querySelector('.checkbox__input')?.value || '';
}

/** @returns {Promise<Record<string, any> | null>} */
async function kaartData(kaart) {
  const bron = kaart.querySelector('[data-vergelijk-data]');
  if (bron) {
    try {
      return JSON.parse(bron.textContent);
    } catch {
      return null;
    }
  }
  const handle = kaartHandle(kaart);
  if (!handle) return null;
  const pagina = kaart.closest('[data-page]')?.getAttribute('data-page') || '';
  const data = await haalPagina(pagina);
  return data?.[handle] ?? null;
}

/**
 * Ververst opgeslagen items met de actuele gegevens van de kaarten op deze pagina: prijzen en
 * specificaties zijn een momentopname van het aanvinken, en anders blijft iemand een oude
 * prijs zien (of mist hij een aanbieding die later is ingegaan).
 */
async function ververs(kaarten) {
  const items = await Promise.all(kaarten.map(kaartData));
  const lijst = lees();
  let gewijzigd = false;
  items.forEach((item) => {
    if (!item) return;
    const plek = lijst.findIndex((i) => i.handle === item.handle);
    if (plek !== -1 && JSON.stringify(lijst[plek]) !== JSON.stringify(item)) {
      lijst[plek] = item;
      gewijzigd = true;
    }
  });
  /* schrijf() stuurt het wijzigingsevent, dus sync loopt nog één keer; dan is er niets
     meer te verversen (de paginadata is gecachet) en stopt het. */
  if (gewijzigd) schrijf(lijst);
}

function initCollectie() {
  /* De widget pas bouwen als we weten dat hij nodig is: in de control-arm hoort er geen
     extra element in de body te staan. @type {ReturnType<typeof bouwTray> | null} */
  let tray = null;

  /* Delegatie op document, geen listener per kaart. Het thema VERVANGT het kaartraster
     bij filteren en bij "Toon meer producten" (morph); listeners op de oude kaarten zijn
     dan weg en een vinkje op een nieuwe kaart deed niets. Dat is dezelfde reden waarom
     de add-to-cart-knop in blocks/product-button.liquid met document-delegatie werkt. */
  document.addEventListener('change', async (gebeurtenis) => {
    const vink = gebeurtenis.target;
    if (!vink.matches?.('.checkbox__input')) return;
    const kaart = vink.closest('[data-vergelijk-kaart]');
    if (!kaart) return;
    /* De stand vóór het wachten op de data vastleggen: een sync van een ander vinkje kan dit
       vinkje intussen terugzetten naar wat er (nog) in de opslag staat. */
    const aan = vink.checked;
    const item = await kaartData(kaart);
    if (!item) return;

    const lijst = lees();
    const staat = lijst.findIndex((i) => i.handle === item.handle);
    if (aan && staat === -1) {
      lijst.push(item);
    } else if (!aan && staat !== -1) {
      lijst.splice(staat, 1);
    } else {
      return; // stand was al goed; geen schrijfactie en geen dubbel event
    }
    schrijf(lijst);
    meld('hds:vergelijk_tick', {
      actie: aan ? 'aan' : 'uit',
      product_id: item.id ?? '',
      handle: item.handle,
      aantal: lijst.length,
    });
  });

  /* `magVerversen` staat uit als de aanleiding uit een andere tab komt. Zie de
     `storage`-listener hieronder. */
  const sync = async (magVerversen = true) => {
    const alle = [...document.querySelectorAll('[data-vergelijk-kaart]')];
    if (alle.length === 0 || experiment.arm === 'control') return;
    /* Een luie kaart weet zelf niet of het product kenmerken heeft; dat staat in de
       paginadata (null = geen). Pas daarna indelen, zodat de blootstelling net als vroeger
       alleen hangt aan een kaart waar het vinkje ook echt kan staan. */
    const geschikt = await Promise.all(
      alle.map(async (kaart) => !kaart.hasAttribute('data-vergelijk-lui') || (await kaartData(kaart)) !== null)
    );
    const kaarten = alle.filter((_, i) => geschikt[i]);
    if (kaarten.length === 0) return;
    /* Control-arm: geen vinkjes, geen widget, geen verdere listeners. De kaart is dan
       precies de kaart van vóór deze test. */
    if (!bepaalArm(kaarten[0])) return;

    const lijst = lees();
    /* Opgeslagen gegevens verversen zolang het product op deze pagina staat (zie ververs()),
       alleen voor kaarten die al in de vergelijking zitten. */
    const opDezePagina = [];
    kaarten.forEach((kaart) => {
      // Treatment: het vinkje uit zijn verborgen stand halen (buiten de test staat het al open).
      kaart.hidden = false;
      const vink = kaart.querySelector('.checkbox__input');
      const handle = kaartHandle(kaart);
      if (!vink || !handle) return;
      const plek = lijst.findIndex((i) => i.handle === handle);
      vink.checked = plek !== -1;
      if (plek !== -1) opDezePagina.push(kaart);
    });
    (tray ??= bouwTray()).ververs(lijst);
    if (magVerversen && opDezePagina.length) void ververs(opDezePagina);
  };

  document.addEventListener('hds:vergelijk-gewijzigd', () => void sync());
  document.addEventListener('filter:update', () => void sync());
  /* Een `storage`-event komt altijd uit een ándere tab. Daar niet op terugschrijven: de
     rijen komen uit `collection.filters`, dus twee tabs op verschillende collecties zien
     van hetzelfde product een andere specificatieset. Schreven ze allebei terug, dan
     stuurden ze elkaar om beurten een nieuwe versie en bleven ze over en weer schrijven
     zolang beide tabs openstaan. Hier alleen opnieuw tekenen. */
  window.addEventListener('storage', () => void sync(false));

  /* Verse kaarten na een morph moeten hun vinkje terugkrijgen. Alleen kijken naar mutaties
     die ook echt een kaart binnenbrengen: een observer over de hele body vuurt ook bij het
     openen van de cart-drawer, bij de filters en bij elke lazy-geladen sectie, en een volle
     sync per keer betekent tientallen JSON.parse-aanroepen voor niets. Dit filtert dat weg
     én maakt de oude uitzondering voor de widget zelf overbodig. */
  let wacht;
  new MutationObserver((records) => {
    if (experiment.arm === 'control') return;
    const raaktKaarten = records.some((record) =>
      [...record.addedNodes].some(
        (node) =>
          node.nodeType === 1 &&
          (node.matches?.('[data-vergelijk-kaart]') || node.querySelector?.('[data-vergelijk-kaart]'))
      )
    );
    if (!raaktKaarten) return;
    clearTimeout(wacht);
    wacht = setTimeout(() => void sync(), 60);
  }).observe(document.body, { childList: true, subtree: true });

  void sync();
}

function bouwTray() {
  const tray = document.createElement('aside');
  tray.className = 'hds-vgl-tray';
  tray.hidden = true;
  tray.setAttribute('aria-label', 'Vergelijking');
  tray.innerHTML = `
    <div class="hds-vgl-tray-kop">
      <button class="hds-vgl-tray-titel" type="button" aria-expanded="true">
        <svg class="hds-vgl-chev" width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
          <path d="M3 4.5L6 8l3-3.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <span>Vergelijken</span>
        <span class="hds-vgl-bubbel">0</span>
      </button>
      <button class="hds-vgl-tray-wis" type="button">Wis alles</button>
    </div>
    <div class="hds-vgl-tray-body">
      <ul class="hds-vgl-tray-lijst"></ul>
      <div class="hds-vgl-tray-voet">
        <a class="button" href="#">Bekijk vergelijking</a>
        <span class="hds-vgl-hint"></span>
      </div>
    </div>`;
  document.body.appendChild(tray);

  const lijstEl = tray.querySelector('.hds-vgl-tray-lijst');
  const badge = tray.querySelector('.hds-vgl-bubbel');
  const knop = tray.querySelector('.hds-vgl-tray-voet .button');
  const hint = tray.querySelector('.hds-vgl-hint');
  const toggle = tray.querySelector('.hds-vgl-tray-titel');

  /* Op een telefoon is de widget alleen nog een pil met «Vergelijken» en het aantal
     (zie CSS): geen lijst, geen uitklappen, één tik naar de vergelijkpagina. Groeit het
     scherm daarna toch, dan staat de widget ingeklapt klaar. */
  const telefoon = window.matchMedia('(max-width: 749px)');
  if (telefoon.matches) {
    tray.classList.add('is-dicht');
    toggle.removeAttribute('aria-expanded');
    toggle.removeAttribute('aria-controls');
  }

  toggle.addEventListener('click', () => {
    if (telefoon.matches) {
      const lijst = lees();
      if (lijst.length) window.location.assign(paginaUrl(lijst));
      return;
    }
    const dicht = tray.classList.toggle('is-dicht');
    toggle.setAttribute('aria-expanded', dicht ? 'false' : 'true');
  });

  tray.querySelector('.hds-vgl-tray-wis').addEventListener('click', () => schrijf([]));

  knop.addEventListener('click', (gebeurtenis) => {
    if (lees().length < MIN) gebeurtenis.preventDefault();
  });

  lijstEl.addEventListener('click', (gebeurtenis) => {
    const weg = gebeurtenis.target.closest('[data-weg]');
    if (!weg) return;
    schrijf(lees().filter((i) => i.handle !== weg.dataset.weg));
  });

  let timer;
  return {
    el: tray,
    ververs(lijst) {
      tray.hidden = lijst.length === 0;
      badge.textContent = lijst.length;
      lijstEl.innerHTML = lijst
        .map(
          (i) => `
        <li>
          ${i.beeld ? `<img class="hds-vgl-thumb" src="${esc(i.beeld)}" alt="" width="40" height="40" loading="lazy">` : '<span class="hds-vgl-thumb"></span>'}
          <span class="hds-vgl-tray-item">
            <span class="hds-vgl-tray-naam">${esc(i.titel)}</span>
            <span class="hds-vgl-tray-prijs">${prijsHtml(i)}</span>
          </span>
          <button class="hds-vgl-weg" type="button" data-weg="${esc(i.handle)}" aria-label="${esc(i.titel)} uit de vergelijking halen">${KRUIS_SVG}</button>
        </li>`
        )
        .join('');
      knop.href = paginaUrl(lijst);
      if (telefoon.matches) {
        toggle.setAttribute(
          'aria-label',
          `Bekijk de vergelijking (${lijst.length} ${lijst.length === 1 ? 'product' : 'producten'})`
        );
      } else {
        toggle.removeAttribute('aria-label');
      }
      knop.setAttribute('aria-disabled', lijst.length < MIN ? 'true' : 'false');
      hint.textContent = lijst.length < MIN ? 'Kies er minstens twee om te vergelijken.' : '';
    },
    melden(tekst) {
      hint.textContent = tekst;
      hint.classList.add('is-waarschuwing');
      clearTimeout(timer);
      timer = setTimeout(() => {
        hint.classList.remove('is-waarschuwing');
        this.ververs(lees());
      }, 4000);
    },
  };
}

/* ------------------------------------------------------------- vergelijkpagina */

function initTabel(houder) {
  /* Vaste rijen zijn optioneel: staat de instelling leeg, dan volgen de rijen de
     kenmerken van de gekozen producten zelf — in de volgorde waarin ze voorkomen, met
     de naam die het metaveld in de winkel heeft. Zo klopt de tabel voor elke categorie
     zonder dat er per categorie iets ingesteld hoeft te worden. */
  let vasteRijen = [];
  try {
    vasteRijen = JSON.parse(houder.dataset.rijen || '[]');
  } catch {
    vasteRijen = [];
  }

  const rijenVoor = (lijst) => {
    if (vasteRijen.length) return vasteRijen;
    const gezien = new Map();
    lijst.forEach((item) => {
      Object.keys(item.specs || {}).forEach((sleutel) => {
        if (gezien.has(sleutel)) return;
        const spec = item.specs[sleutel];
        gezien.set(sleutel, (spec && spec.l) || sleutel);
      });
    });
    return Array.from(gezien, ([sleutel, label]) => ({ sleutel, label, groep: '' }));
  };

  // De arm meenemen in de events van deze pagina; leeg als er geen test loopt.
  experiment.id = houder.dataset.experiment || '';
  setTreatmentShare(experiment.id, houder.dataset.aandeel ?? '');

  /* `?producten=` beperkt de tabel tot een deel van wat er in de opslag staat (een gedeelde
     link). Die beperking moet blijven gelden als de lijst later verandert, dus hij leeft als
     eigen variabele en niet als eenmalige filterslag over `items`. */
  let beperking = (new URLSearchParams(location.search).get('producten') || '').split(',').filter(Boolean);
  const opslagBijStart = lees();
  let ontbreekt = beperking.length
    ? beperking.filter((h) => !opslagBijStart.some((i) => i.handle === h)).length
    : 0;

  const laadItems = () => {
    const opgeslagen = lees();
    if (!beperking.length) return opgeslagen;
    const gekozen = opgeslagen.filter((i) => beperking.includes(i.handle));
    // Geen enkele match (link van een ander apparaat): dan maar tonen wat er wél is.
    return gekozen.length ? gekozen : opgeslagen;
  };

  let items = laadItems();

  let alleenVerschillen = false;
  /* Op een telefoon krijgt de tabel géén labelkolom: de specificatienaam staat als eigen
     regel boven de waarden. Dat scheelt daar bijna een derde van het scherm. De keuze
     zit in de structuur en niet in CSS, want een kolom wegstijlen laat de kop en de
     waarden uit elkaar lopen. */
  const telefoon = () => window.innerWidth < 750;
  let opTelefoon = telefoon();

  /* Nieuwe vorm: { sleutel: { l: label, v: waarde } }. Oudere opslag hield alleen de
     waarde als tekst; die blijft werken tot hij zich ververst. */
  const waarde = (item, sleutel) => {
    const spec = item.specs?.[sleutel];
    if (spec == null) return '';
    return typeof spec === 'object' ? spec.v || '' : String(spec);
  };

  /* Opnieuw tekenen vervangt de hele tabel, en dan begint de scroller weer op nul. Wie
     een product weghaalt terwijl hij rechts in de tabel zit, wil daar blijven staan —
     dus onthouden waar we waren en dat na het tekenen terugzetten (afgekapt op de
     nieuwe breedte, want er is een kolom minder). */
  const positieOnthouden = () => ({
    x: houder.querySelector('.hds-vgl-scroller')?.scrollLeft || 0,
    y: window.scrollY,
  });

  const positieTerug = ({ x, y }) => {
    const venster = houder.querySelector('.hds-vgl-scroller');
    if (venster && x) {
      venster.scrollLeft = Math.min(x, venster.scrollWidth - venster.clientWidth);
    }
    if (Math.round(window.scrollY) !== Math.round(y)) window.scrollTo({ top: y });
  };

  const teken = () => {
    const positie = positieOnthouden();
    if (items.length === 0) {
      houder.innerHTML = `
        <p class="hds-vgl-leeg">
          ${ontbreekt ? 'Deze link is op een ander apparaat gemaakt, dus de gegevens staan niet in deze browser. ' : ''}
          Vink op een collectiepagina minstens twee producten aan om ze naast elkaar te zetten.
          ${houder.dataset.terug
            ? `<a href="${esc(houder.dataset.terug)}">Naar het overzicht</a>`
            : '<button class="hds-vgl-terug" type="button" data-vergelijk-terug>Terug naar het overzicht</button>'}
        </p>`;
      return;
    }

    const groepen = [];
    rijenVoor(items).forEach((rij) => {
      if (!items.some((i) => waarde(i, rij.sleutel))) return;
      const naam = rij.groep || '';
      let groep = groepen.find((g) => g.naam === naam);
      if (!groep) groepen.push((groep = { naam, rijen: [] }));
      groep.rijen.push(rij);
    });

    const smal = telefoon();
    const kolommen = items.length + (smal ? 0 : 1);
    let gelijk = 0;
    let totaal = 0;
    /* De streping telt alleen de rijen die ook echt te zien zijn: met «alleen de
       verschillen» aan vallen de gelijke rijen weg, en een telling over álle rijen zet
       dan twee gekleurde banden onder elkaar. Op een telefoon staat het label boven de
       waarden, dus daar krijgen beide rijen dezelfde tint en lezen ze als één band. */
    let zichtbaar = 0;
    const rijHtml = (rij) => {
      totaal += 1;
      const waarden = items.map((i) => waarde(i, rij.sleutel));
      const zelfde = new Set(waarden).size === 1;
      if (zelfde) gelijk += 1;
      const verborgen = alleenVerschillen && zelfde;
      if (!verborgen) zichtbaar += 1;
      const tint = !verborgen && zichtbaar % 2 === 1 ? ' hds-vgl-rij--tint' : '';
      return `
        <tr class="hds-vgl-labelrij${zelfde ? ' is-gelijk' : ''}${tint}">
          <th colspan="${kolommen}"><span>${esc(rij.label)}</span></th>
        </tr>
        <tr class="hds-vgl-rij${zelfde ? ' is-gelijk' : ''}${tint}">
          ${smal ? '' : `<th scope="row">${esc(rij.label)}</th>`}
          ${waarden.map((w) => `<td>${w ? esc(w) : '<span class="hds-vgl-leeg-cel">—</span>'}</td>`).join('')}
        </tr>`;
    };

    const lichaam = groepen
      .map(
        (groep) => `
        <tbody>
          ${groep.naam ? `<tr class="hds-vgl-groep"><th colspan="${kolommen}"><span>${esc(groep.naam)}</span></th></tr>` : ''}
          ${groep.rijen.map(rijHtml).join('')}
        </tbody>`
      )
      .join('');

    /* Eén colgroup voor beide tabellen: met table-layout: fixed lopen de kolommen van de
       kopstrook en de specificaties dan exact gelijk. */
    const kolomgroep = `<colgroup>${smal ? '' : '<col class="hds-vgl-col-label">'}${items.map(() => '<col>').join('')}</colgroup>`;

    houder.innerHTML = `
      <div class="hds-vgl-balk">
        <span class="hds-vgl-aantal"><strong>${items.length}</strong> producten vergeleken</span>
        ${houder.dataset.terug
          ? `<a class="button-secondary" href="${esc(houder.dataset.terug)}">Product toevoegen</a>`
          : '<button class="button-secondary" type="button" data-vergelijk-terug>Product toevoegen</button>'}
        <button class="hds-vgl-schakelaar" type="button" aria-pressed="${alleenVerschillen}">
          <span class="hds-vgl-spoor" aria-hidden="true"></span>
          <span>Toon alleen de verschillen</span>
        </button>
      </div>
      <div class="hds-vgl-venster">
        <div class="hds-vgl-pijlen">
          <button class="hds-vgl-pijl hds-vgl-pijl--vorige" type="button" data-schuif="-1" aria-label="Vorige producten" hidden>
            <svg width="10" height="16" viewBox="0 0 10 16" fill="none" aria-hidden="true"><path d="M7.5 1.5L2 8l5.5 6.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </button>
          <button class="hds-vgl-pijl hds-vgl-pijl--volgende" type="button" data-schuif="1" aria-label="Volgende producten" hidden>
            <svg width="10" height="16" viewBox="0 0 10 16" fill="none" aria-hidden="true"><path d="M2.5 1.5L8 8l-5.5 6.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </button>
        </div>
        <div class="hds-vgl-kopstrip">
          <table class="hds-vgl-tabel hds-vgl-kop-tabel">
            ${kolomgroep}
            <thead>
              <tr>
                ${smal ? '' : '<th class="hds-vgl-hoek" scope="col"><span>Specificatie</span></th>'}
                ${items
                  .map(
                    (i) => `
                  <th scope="col">
                    <button class="hds-vgl-weg hds-vgl-kop-weg" type="button" data-weg="${esc(i.handle)}" aria-label="${esc(i.titel)} uit de vergelijking halen">${KRUIS_SVG}</button>
                    ${i.beeld ? `<img src="${esc(i.beeld)}" alt="" width="72" height="72" loading="lazy">` : ''}
                    <a class="hds-vgl-kop-naam" href="${esc(i.url)}" data-handle="${esc(i.handle)}">${esc(i.titel)}</a>
                    <span class="hds-vgl-kop-prijs">${prijsHtml(i)}</span>
                  </th>`
                  )
                  .join('')}
              </tr>
            </thead>
          </table>
        </div>
        <div class="hds-vgl-scroller">
        <table class="hds-vgl-tabel${alleenVerschillen ? ' toon-verschillen' : ''}">
          <caption class="hds-vgl-caption">
            Specificaties per kolom, in deze volgorde: ${items.map((i) => esc(i.titel)).join(', ')}.
          </caption>
          ${kolomgroep}
          ${lichaam}
        </table>
        </div>
      </div>`;

    naTekenen();
    positieTerug(positie);
  };

  /* Terug zonder ingestelde URL: één stap in de geschiedenis, dus terug naar de
     collectie waar de klant vandaan kwam. */
  document.addEventListener('click', (gebeurtenis) => {
    if (gebeurtenis.target.closest('[data-vergelijk-terug]')) {
      if (history.length > 1) {
        history.back();
      } else {
        window.location.href = '/collections/all';
      }
    }
  });

  houder.addEventListener('click', (gebeurtenis) => {
    const naarPdp = gebeurtenis.target.closest('.hds-vgl-kop-naam');
    if (naarPdp) {
      meld('hds:vergelijk_naar_pdp', {
        bron: 'tabel',
        handle: naarPdp.dataset.handle ?? '',
        aantal: items.length,
      });
      onthoudGebruik();
      /* Het event loopt via de sandbox van de Custom Pixel naar GTM; navigeert de pagina
         meteen, dan sterft die sandbox voordat GA4 het verstuurt. Een gewone klik wacht
         daarom even. Een klik voor een nieuw tabblad laat deze pagina staan. */
      const nieuwTabblad =
        gebeurtenis.button !== 0 ||
        gebeurtenis.metaKey ||
        gebeurtenis.ctrlKey ||
        gebeurtenis.shiftKey ||
        gebeurtenis.altKey ||
        naarPdp.target === '_blank';
      if (!nieuwTabblad && !gebeurtenis.defaultPrevented) {
        gebeurtenis.preventDefault();
        setTimeout(() => {
          window.location.href = naarPdp.href;
        }, 300);
      }
      return;
    }
    const weg = gebeurtenis.target.closest('[data-weg]');
    if (weg && !weg.disabled) {
      const handle = weg.dataset.weg;
      /* De melding over een link van een ander apparaat hoort bij het openen van de
         pagina, niet bij wat de bezoeker daarna zelf weghaalt. */
      ontbreekt = 0;
      beperking = beperking.filter((h) => h !== handle);
      /* Alleen dít product uit de opslag halen. `items` is bij een gedeelde link maar een
         deel van de selectie, en die lijst terugschrijven wiste stilletjes al het andere
         dat de bezoeker had aangevinkt. schrijf() stuurt het wijzigingsevent, dus het
         opnieuw laden en tekenen gebeurt daar. */
      schrijf(lees().filter((i) => i.handle !== handle));
      history.replaceState(null, '', paginaUrl(items));
      return;
    }
    const schuif = gebeurtenis.target.closest('[data-schuif]');
    if (schuif) {
      const venster = houder.querySelector('.hds-vgl-scroller');
      const stap = parseFloat(getComputedStyle(houder).getPropertyValue('--kolom')) || 208;
      venster.scrollBy({ left: +schuif.dataset.schuif * stap });
      /* Niet alleen op het scroll-event vertrouwen: bij een programmatische sprong komt
         dat niet altijd door, en dan blijft de verkeerde pijl staan. */
      setTimeout(werkPijlenBij, 60);
      return;
    }
    if (gebeurtenis.target.closest('.hds-vgl-schakelaar')) {
      alleenVerschillen = !alleenVerschillen;
      teken();
    }
  });

  /* Hoeveel kolommen er naast elkaar passen is een keuze, geen gevolg van de
     kolombreedte: we meten de beschikbare ruimte en delen die over vier kolommen
     (tablet drie, telefoon twee). Daardoor staat er nooit een halve kolom in beeld en
     blader je met de pijlen naar de rest. */
  const pasKolommenAan = () => {
    const venster = houder.querySelector('.hds-vgl-scroller');
    const hoek = houder.querySelector('.hds-vgl-hoek');
    if (!venster) return;
    const zichtbaar = Math.min(items.length, window.innerWidth >= 1080 ? 4 : window.innerWidth >= 750 ? 3 : 2);
    /* Op telefoon staat er geen labelkolom in de tabel, dus telt zijn breedte niet mee. */
    const hoekBreed = hoek ? hoek.offsetWidth : 0;
    const ruimte = venster.clientWidth - hoekBreed;
    if (ruimte > 0 && zichtbaar > 0) {
      houder.style.setProperty('--kolom', Math.floor(ruimte / zichtbaar) + 'px');
    }
    /* Hoeveel de kop in totaal moet meeschuiven; de CSS-animatie loopt van 0 tot dit. */
    houder.style.setProperty('--hds-vgl-schuif', venster.scrollWidth - venster.clientWidth + 'px');
    werkPijlenBij();
  };

  const werkPijlenBij = () => {
    const venster = houder.querySelector('.hds-vgl-scroller');
    const vorige = houder.querySelector('.hds-vgl-pijl--vorige');
    const volgende = houder.querySelector('.hds-vgl-pijl--volgende');
    if (!venster || !vorige || !volgende) return;
    const rest = venster.scrollWidth - venster.clientWidth - venster.scrollLeft;
    vorige.hidden = venster.scrollLeft < 8;
    volgende.hidden = rest < 8;
  };

  /* Kent de browser scroll-gestuurde animaties? Dan schuift de kop mee via CSS (op de
     compositor) en houdt JS zich er verder buiten. Zo niet, dan doen we het zelf.
     De @supports-vraag alleen is niet genoeg: we kijken ook of de animatie zijn
     scroll-tijdlijn echt te pakken heeft, zodat een halve implementatie niet in een
     kop eindigt die blijft staan. */
  const cssSchuiftMisschien = CSS.supports('timeline-scope', '--hds-vgl-x');
  const cssSchuiftEcht = () => {
    const kopTabel = houder.querySelector('.hds-vgl-kop-tabel');
    if (!cssSchuiftMisschien || !kopTabel?.getAnimations) return false;
    return kopTabel
      .getAnimations()
      .some((a) => a.animationName === 'hds-vgl-kop-mee' && a.timeline && a.playState !== 'idle');
  };

  const naTekenen = () => {
    pasKolommenAan();
    /* Nieuwe strook na het hertekenen, dus de gemeten afstand opnieuw aanbrengen. */
    kopOnder = -1;
    volgSiteKop();
    const venster = houder.querySelector('.hds-vgl-scroller');
    const strip = houder.querySelector('.hds-vgl-kopstrip');
    if (!venster) return;

    /* Pijlen niet in het scroll-event bijwerken: dat leest bij elke veegframe de
       breedtes op en dat kost precies de tijd die de veeg vloeiend moet houden. Op een
       telefoon staan ze helemaal uit, dus daar meten we niks. */
    const geenPijlen = window.matchMedia('(max-width: 749px)');
    let gepland = false;
    const planPijlen = () => {
      if (geenPijlen.matches || gepland) return;
      gepland = true;
      requestAnimationFrame(() => {
        gepland = false;
        werkPijlenBij();
      });
    };

    if (cssSchuiftEcht() || !strip) {
      venster.addEventListener('scroll', planPijlen, { passive: true });
      return;
    }

    /* Terugval: kop en tabel zelf aan elkaar knopen. Wie de veeg begint is de baas, en
       blijft dat tot de beweging is uitgelopen — anders sturen de twee elkaar bij en
       schokt de kop. */
    let baas = null;
    let rust;
    const koppel = (van, naar) =>
      van.addEventListener(
        'scroll',
        () => {
          if (baas && baas !== van) return;
          baas = van;
          naar.scrollLeft = van.scrollLeft;
          clearTimeout(rust);
          rust = setTimeout(() => {
            baas = null;
          }, 120);
          planPijlen();
        },
        { passive: true }
      );
    koppel(venster, strip);
    koppel(strip, venster);
  };

  /* De productkop plakt ONDER de site-kop, niet eroverheen. Die afstand is niet vast:
     Horizon maakt zijn kop los bij omlaag scrollen en laat hem bij omhoog scrollen weer
     vastklikken, dus we lezen bij elke scroll waar de onderkant staat. Per frame één
     meting, en alleen schrijven als de waarde echt verandert — buiten de overgang is dat
     bijna nooit, dus het kost tijdens gewoon scrollen niets. */
  const siteKop = document.querySelector(
    'header.shopify-section-group-header-group, .header-section, .shopify-section-header'
  );
  let kopOnder = -1;
  let kopGepland = false;
  const volgSiteKop = () => {
    const strip = houder.querySelector('.hds-vgl-kopstrip');
    if (!strip) return;
    const onder = siteKop ? Math.max(0, Math.round(siteKop.getBoundingClientRect().bottom)) : 0;
    if (onder === kopOnder) return;
    kopOnder = onder;
    strip.style.setProperty('--hds-vgl-kop-top', onder + 'px');
  };
  const planSiteKop = () => {
    if (kopGepland) return;
    kopGepland = true;
    requestAnimationFrame(() => {
      kopGepland = false;
      volgSiteKop();
    });
  };
  window.addEventListener('scroll', planSiteKop, { passive: true });

  window.addEventListener('resize', () => {
    /* Van telefoon naar tablet verandert de tabelstructuur, dus dan opnieuw tekenen. */
    if (telefoon() !== opTelefoon) {
      opTelefoon = telefoon();
      teken();
      return;
    }
    pasKolommenAan();
  });

  document.addEventListener('hds:vergelijk-gewijzigd', () => {
    items = laadItems();
    teken();
  });

  teken();

  /* Één keer melden dat de tabel er stond, mét hoeveel producten. GA4 ziet de paginaweergave
     ook zonder dit event, maar niet de arm en niet het aantal — en juist die twee maken het
     verschil tussen "de pagina is bezocht" en "er is echt vergeleken". */
  meld('hds:vergelijk_tabel_open', { aantal: items.length, ontbreekt });
}

/* ---------------------------------------------------------------------- start */

const houder = document.querySelector('[data-vergelijk-tabel]');
if (houder) initTabel(houder);

/* Ook starten als er nog geen kaart in de DOM staat: bij een morph komen ze later. Niet op
   de vergelijkpagina zelf: die draagt vaak óók een productraster (gerelateerde producten),
   en dan zou de widget daar bovenop de tabel komen te staan. */
if (!houder && document.querySelector('[data-vergelijk-kaart], .product-grid, .resource-list')) {
  initCollectie();
}
